import React, { useState, useEffect } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { Gavel, Clock, Trophy, XCircle, ArrowLeft, Loader2, ExternalLink, Send, CheckCircle, AlertCircle, DollarSign } from 'lucide-react';
import axios from '../lib/axios';
import { useAuth, API } from '../App';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const MyBidsPage = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ bids: 0, offers: 0, winning: 0, pending_offers: 0 });
  const [activeTab, setActiveTab] = useState('all');

  useEffect(() => {
    if (user) {
      fetchBidsAndOffers();
    }
  }, [user, token, activeTab]);

  const fetchBidsAndOffers = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/users/me/bids-and-offers?tab=${activeTab}`);
      setItems(response.data.items || []);
      setStats(response.data.stats || { bids: 0, offers: 0, winning: 0, pending_offers: 0 });
    } catch (error) {
      console.error('Failed to fetch bids and offers:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  const getStatusBadge = (item) => {
    if (item.type === 'bid') {
      // Auction bid statuses
      if (item.listing_status === 'sold') {
        return item.status === 'won' ? (
          <span className="px-3 py-1 bg-green-500 text-white text-xs font-semibold rounded-full flex items-center gap-1">
            <Trophy className="w-3 h-3" />
            Won
          </span>
        ) : (
          <span className="px-3 py-1 bg-red-500/20 text-red-500 text-xs font-semibold rounded-full flex items-center gap-1">
            <XCircle className="w-3 h-3" />
            Lost
          </span>
        );
      }
      
      if (item.is_ended) {
        return item.status === 'won' ? (
          <span className="px-3 py-1 bg-green-500 text-white text-xs font-semibold rounded-full flex items-center gap-1">
            <Trophy className="w-3 h-3" />
            Won
          </span>
        ) : (
          <span className="px-3 py-1 bg-gray-500/20 text-gray-500 text-xs font-semibold rounded-full">
            Ended
          </span>
        );
      }
      
      return item.is_winning ? (
        <span className="px-3 py-1 bg-green-500/20 text-green-500 text-xs font-semibold rounded-full flex items-center gap-1">
          <Trophy className="w-3 h-3" />
          Winning
        </span>
      ) : (
        <span className="px-3 py-1 bg-orange-500/20 text-orange-500 text-xs font-semibold rounded-full">
          Outbid
        </span>
      );
    } else {
      // Offer statuses
      switch (item.status) {
        case 'accepted':
          return (
            <span className="px-3 py-1 bg-green-500/20 text-green-500 text-xs font-semibold rounded-full flex items-center gap-1">
              <CheckCircle className="w-3 h-3" />
              Accepted
            </span>
          );
        case 'rejected':
        case 'declined':
          return (
            <span className="px-3 py-1 bg-red-500/20 text-red-500 text-xs font-semibold rounded-full flex items-center gap-1">
              <XCircle className="w-3 h-3" />
              Declined
            </span>
          );
        case 'countered':
          return (
            <span className="px-3 py-1 bg-amber-500/20 text-amber-500 text-xs font-semibold rounded-full flex items-center gap-1">
              <AlertCircle className="w-3 h-3" />
              Counter Offer
            </span>
          );
        default:
          return (
            <span className="px-3 py-1 bg-blue-500/20 text-blue-500 text-xs font-semibold rounded-full flex items-center gap-1">
              <Clock className="w-3 h-3" />
              Pending
            </span>
          );
      }
    }
  };

  const formatTimeRemaining = (endTime) => {
    if (!endTime) return 'No end time';
    
    try {
      const end = new Date(endTime);
      const now = new Date();
      const diff = end - now;
      
      if (diff <= 0) return 'Ended';
      
      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      
      if (days > 0) return `${days}d ${hours}h left`;
      if (hours > 0) return `${hours}h ${minutes}m left`;
      return `${minutes}m left`;
    } catch {
      return 'Invalid date';
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      return new Date(dateStr).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    } catch {
      return '';
    }
  };

  const tabs = [
    { id: 'all', label: 'All Active', count: (stats.bids + stats.offers) - stats.archived },
    { id: 'bids', label: 'Auction Bids', count: stats.bids - stats.archived },
    { id: 'offers', label: 'Offers Sent', count: stats.offers },
    { id: 'archived', label: 'Archived', count: stats.archived }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="container mx-auto max-w-6xl px-4 py-8">
        {/* Header */}
        <div className="flex items-center gap-3 sm:gap-4 mb-8">
          <button
            onClick={() => navigate(-1)}
            className="p-2 hover:bg-secondary rounded-full transition-colors flex-shrink-0"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="min-w-0">
            <h1 className="text-lg sm:text-2xl font-bold text-foreground flex items-center gap-2 sm:gap-3">
              <Gavel className="w-5 h-5 sm:w-7 sm:h-7 text-primary flex-shrink-0" />
              <span>My Bids & Offers</span>
            </h1>
            <p className="text-muted-foreground text-sm mt-1">
              Track your auction bids and listing offers
            </p>
          </div>
        </div>

        {/* Stats Summary */}
        {!loading && (stats.bids > 0 || stats.offers > 0) && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
            <div className="bg-card rounded-xl p-4 border border-border text-center">
              <p className="text-2xl font-bold text-foreground">{stats.bids}</p>
              <p className="text-sm text-muted-foreground">Auction Bids</p>
            </div>
            <div className="bg-card rounded-xl p-4 border border-border text-center">
              <p className="text-2xl font-bold text-green-500">{stats.winning}</p>
              <p className="text-sm text-muted-foreground">Winning</p>
            </div>
            <div className="bg-card rounded-xl p-4 border border-border text-center">
              <p className="text-2xl font-bold text-blue-500">{stats.offers}</p>
              <p className="text-sm text-muted-foreground">Offers Sent</p>
            </div>
            <div className="bg-card rounded-xl p-4 border border-border text-center">
              <p className="text-2xl font-bold text-amber-500">{stats.pending_offers}</p>
              <p className="text-sm text-muted-foreground">Pending</p>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b border-border pb-3 overflow-x-auto">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 rounded-lg font-medium text-sm whitespace-nowrap transition-colors ${
                activeTab === tab.id
                  ? 'bg-primary text-white'
                  : 'bg-secondary text-foreground hover:bg-secondary/80'
              }`}
            >
              {tab.label}
              {tab.count > 0 && (
                <span className={`ml-2 px-2 py-0.5 rounded-full text-xs ${
                  activeTab === tab.id ? 'bg-white/20' : 'bg-muted'
                }`}>
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Items List */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 text-primary animate-spin" />
          </div>
        ) : items.length === 0 ? (
          <div className="bg-card rounded-2xl p-12 border border-border text-center">
            <Gavel className="w-16 h-16 text-muted-foreground mx-auto mb-4 opacity-50" />
            <h2 className="text-xl font-semibold text-foreground mb-2">
              {activeTab === 'bids' ? 'No auction bids yet' : 
               activeTab === 'offers' ? 'No offers sent yet' : 
               'No bids or offers yet'}
            </h2>
            <p className="text-muted-foreground mb-6">
              {activeTab === 'bids' 
                ? "You haven't placed any bids on auction listings yet."
                : activeTab === 'offers'
                ? "You haven't made any offers on listings yet."
                : "Start bidding on auctions or making offers on listings."}
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link
                to="/marketplace?listing_type=auction"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-primary text-white rounded-xl hover:opacity-90 transition-opacity"
              >
                <Gavel className="w-4 h-4" />
                Browse Auctions
              </Link>
              <Link
                to="/marketplace"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-secondary text-foreground rounded-xl hover:bg-secondary/80 transition-colors"
              >
                <DollarSign className="w-4 h-4" />
                Browse Listings
              </Link>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {items.map((item, index) => (
              <div
                key={`${item.type}-${item.id}-${index}`}
                className={`block bg-card rounded-xl border transition-colors overflow-hidden group ${
                  item.listing_exists === false 
                    ? 'border-red-500/30 opacity-60' 
                    : item.is_archived 
                    ? 'border-border opacity-75'
                    : 'border-border hover:border-primary/50'
                }`}
              >
                {item.listing_exists !== false ? (
                  <Link to={`/listing/${item.listing_id}`} className="flex gap-4 p-4">
                    {/* Image */}
                    <div className="w-24 h-24 sm:w-32 sm:h-32 rounded-lg overflow-hidden bg-muted flex-shrink-0 relative">
                      {item.listing_image ? (
                        <img
                          src={item.listing_image}
                          alt={item.listing_title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          {item.type === 'bid' ? (
                            <Gavel className="w-8 h-8 text-muted-foreground" />
                          ) : (
                            <Send className="w-8 h-8 text-muted-foreground" />
                          )}
                        </div>
                      )}
                      {/* Type badge */}
                      <span className={`absolute top-2 left-2 px-2 py-0.5 text-xs font-medium rounded ${
                        item.type === 'bid' 
                          ? 'bg-purple-500 text-white' 
                          : 'bg-blue-500 text-white'
                      }`}>
                        {item.type === 'bid' ? 'Bid' : 'Offer'}
                      </span>
                      {item.is_archived && (
                        <span className="absolute top-2 right-2 px-2 py-0.5 text-xs font-medium rounded bg-gray-500 text-white">
                          Archived
                        </span>
                      )}
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <h3 className="font-semibold text-foreground truncate group-hover:text-primary transition-colors">
                          {item.listing_title || item.title}
                        </h3>
                        {getStatusBadge(item)}
                      </div>

                      <p className="text-sm text-muted-foreground capitalize mb-3">
                        {item.category?.replace('-', ' ')}
                    </p>

                    {item.type === 'bid' ? (
                      /* Auction Bid Info */
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <p className="text-muted-foreground">Current Bid</p>
                          <p className="font-semibold text-foreground">
                            {item.current_bid > 0 
                              ? `$${item.current_bid?.toLocaleString('en-US', { minimumFractionDigits: 2 })}`
                              : 'No bids yet'}
                          </p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">Your Highest</p>
                          <p className={`font-semibold ${item.is_winning ? 'text-green-500' : 'text-foreground'}`}>
                            ${item.my_amount?.toLocaleString('en-US', { minimumFractionDigits: 2 }) || '0.00'}
                          </p>
                        </div>
                      </div>
                    ) : (
                      /* Offer Info */
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <p className="text-muted-foreground">Asking Price</p>
                          <p className="font-semibold text-foreground">
                            {item.asking_price > 0 
                              ? `$${item.asking_price?.toLocaleString('en-US', { minimumFractionDigits: 2 })}`
                              : 'Price on request'}
                          </p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">Your Offer</p>
                          <p className="font-semibold text-blue-500">
                            ${item.my_amount?.toLocaleString('en-US', { minimumFractionDigits: 2 }) || '0.00'}
                          </p>
                        </div>
                      </div>
                    )}

                    <div className="flex items-center justify-between mt-3 pt-3 border-t border-border">
                      {item.type === 'bid' ? (
                        <div className="flex items-center gap-1 text-sm text-muted-foreground">
                          <Clock className="w-4 h-4" />
                          <span className={item.is_ended ? 'text-gray-500' : ''}>
                            {formatTimeRemaining(item.auction_end_time)}
                          </span>
                        </div>
                      ) : (
                        <span className="text-sm text-muted-foreground">
                          Sent {formatDate(item.created_at)}
                        </span>
                      )}
                      
                      {item.listing_status === 'sold' && (
                        <span className="text-sm text-red-500 font-medium">Sold</span>
                      )}
                      {item.listing_status === 'reserved' && (
                        <span className="text-sm text-amber-500 font-medium">Reserved</span>
                      )}
                      {item.listing_status === 'deleted' && (
                        <span className="text-sm text-gray-500 font-medium">Deleted</span>
                      )}
                    </div>

                    {/* Offer message preview */}
                    {item.type === 'offer' && item.message && (
                      <p className="text-xs text-muted-foreground mt-2 truncate">
                        "{item.message}"
                      </p>
                    )}
                  </div>

                  {/* Arrow indicator */}
                  <div className="hidden sm:flex items-center">
                    <ExternalLink className="w-5 h-5 text-muted-foreground group-hover:text-primary transition-colors" />
                  </div>
                  </Link>
                ) : (
                  /* Deleted listing - non-clickable */
                  <div className="flex gap-4 p-4">
                    <div className="w-24 h-24 sm:w-32 sm:h-32 rounded-lg overflow-hidden bg-red-500/10 flex-shrink-0 relative flex items-center justify-center">
                      <XCircle className="w-8 h-8 text-red-500/50" />
                      <span className="absolute top-2 left-2 px-2 py-0.5 text-xs font-medium rounded bg-gray-500 text-white">
                        {item.type === 'bid' ? 'Bid' : 'Offer'}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <h3 className="font-semibold text-gray-500">
                          Listing no longer exists
                        </h3>
                        <span className="px-3 py-1 bg-gray-500/20 text-gray-500 text-xs font-semibold rounded-full">
                          Deleted
                        </span>
                      </div>
                      <p className="text-sm text-muted-foreground mb-2">
                        Your {item.type === 'bid' ? 'bid' : 'offer'} of <span className="font-medium">${item.amount?.toFixed(2)}</span>
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Placed on {formatDate(item.created_at)}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
};

export default MyBidsPage;
